import axios, { AxiosError } from "axios";
import { pickBy } from "lodash";
import {
  CreateParams,
  DeleteManyParams,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetManyParams,
  GetManyReferenceParams,
  GetOneParams,
  HttpError,
  Identifier,
  UpdateManyResult,
} from "ra-core";
// import crypto from 'crypto-js';
import jsonServerProvider from "ra-data-json-server";
import { DataProvider } from "react-admin";
import { getToken } from "../utils/token.util";
export const API = axios.create({
  baseURL: import.meta.env.PROD ? "https://api.sisdatweb.com" : "http://localhost:8000",
});

API.interceptors.request.use((config) => {
  const authorization = getToken();
  if (authorization) config.headers.Authorization = authorization;
  return config;
});

export interface UpdateManyArrayParams {
  ids: Identifier[];
  data: any[];
}
export interface CreateManyParams {
  data: any[];
}

/**
 * Remove entradas vazias de arrays (emails, phones) antes de enviar ao backend
 */
const cleanArrayFields = (data: any) => {
  const cleaned = { ...data };
  
  // Limpa array de emails secundários
  if (Array.isArray(cleaned.emails)) {
    cleaned.emails = cleaned.emails
      .filter((item: any) => item && item.email && item.email.trim() !== '')
      .map((item: any) => {
        // Remove id se for null (novos items do React Admin)
        const { id, ...rest } = item;
        return id === null || id === undefined ? rest : item;
      });
  }
  
  // Limpa array de telefones
  if (Array.isArray(cleaned.phones)) {
    cleaned.phones = cleaned.phones
      .filter((item: any) => item && item.phone && item.phone.trim() !== '')
      .map((item: any) => {
        // Remove id se for null (novos items do React Admin)
        const { id, ...rest } = item;
        return id === null || id === undefined ? rest : item;
      });
  }
  
  // Limpa company se id for null
  if (cleaned.company && cleaned.company.id === null) {
    delete cleaned.company;
  }
  
  // Limpa companyId se for null
  if (cleaned.companyId === null) {
    delete cleaned.companyId;
  }
  
  // Remove licenses (não deve ser enviado no update de user)
  if (cleaned.licenses) {
    delete cleaned.licenses;
  }
  
  return cleaned;
};

/**
 * Verifica se dois arrays são equivalentes (mesmo conteúdo)
 */
const arraysEqual = (a: any[], b: any[]) => {
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

const normalizeOrderItems = (items: any[] = []) => {
  if (!Array.isArray(items)) return [];

  return items.map((item: any) => ({
    product_id: item?.product_id ?? item?.productId,
    product_name: item?.product_name ?? item?.productName ?? item?.product?.name ?? item?.product?.productName,
    quantity: Number(item?.quantity ?? 0),
    price: Number(item?.price ?? 0),
  }));
};

const normalizeOrderRecord = (record: any) => {
  if (!record || typeof record !== "object") return record;

  const billingAddress = record.billing_address ?? {};
  const items = normalizeOrderItems(record.items || []);

  return {
    ...record,
    items,
    userId: record.userId ?? record.user_id,
    orderDate: record.orderDate ?? record.order_date,
    paymentMethod: record.paymentMethod ?? record.payment_method,
    totalDiscount: record.totalDiscount ?? record.total_discount,
    totalAmount: record.totalAmount ?? record.total_amount,
    billingStreet: record.billingStreet ?? billingAddress.street,
    billingNumber: record.billingNumber ?? billingAddress.number,
    billingComplement: record.billingComplement ?? billingAddress.complement,
    billingNeighborhood: record.billingNeighborhood ?? billingAddress.neighborhood,
    billingCity: record.billingCity ?? billingAddress.city,
    billingState: record.billingState ?? billingAddress.state,
    billingPostalCode: record.billingPostalCode ?? billingAddress.postal_code,
    billingCountry: record.billingCountry ?? billingAddress.country,
  };
};

/**
 * Resolve API base path by resource.
 * Most resources use /admin, while orders currently lives outside this namespace.
 */
const getResourceBasePath = (resource: string) => {
  if (resource === "orders") {
    return `/${resource}`;
  }

  return `/admin/${resource}`;
};

export const apiDataProvider: DataProvider = {
  ...jsonServerProvider(API.defaults.baseURL),

  /**
   * Requisita um único registro através de seu ID
   *
   * @param resource
   * @param id
   * @returns
   */
  async getOne(resource: string, { id }: GetOneParams) {
    const basePath = getResourceBasePath(resource);
    const { data } = await API.get(`${basePath}/${id}`).catch(apiDataProvider.formatError);
    return { data: resource === "orders" ? normalizeOrderRecord(data) : data };
  },

  /**
   * Requisita multiplos registros a API filtrando por id, ex: path?id=1&id=2
   * @param resource
   * @param params
   * @returns
   */
  async getMany(resource: string, { ids }: GetManyParams) {
    // Special handling for license-audit-logs
    if (resource === 'license-audit-logs') {
      const queryString = ids.map(id => `licenseIds[]=${encodeURIComponent(id)}`).join('&');
      const { data } = await API.get(`/admin/licenses/audit-logs/get-many?${queryString}`)
        .catch(apiDataProvider.formatError);
      return { data: Array.isArray(data) ? data : (data.list || data) };
    }
    
    // Build query string manually to ensure ids=1&ids=2 format
    const queryString = ids.map(id => `ids=${encodeURIComponent(id)}`).join('&');
    const basePath = getResourceBasePath(resource);
    const { data } = await API.get(`${basePath}/get-many?${queryString}`)
      .catch(apiDataProvider.formatError);
    
    // Handle both array responses and object responses with 'list' property
    return { data: Array.isArray(data) ? data : (data.list || data) };
  },

  /**
   * Requisita multiplos registros a API filtrando por id, ex: path?id=1&id=2
   * @param resource
   * @param params
   * @returns
   */
  async getManyReference(resource: string, { id, target, filter, pagination, sort }: GetManyReferenceParams) {
    const paginationObj = pagination || { page: 1, perPage: 10 };
    const params = new URLSearchParams({
      [target]: id,
      ...filter,
      page: paginationObj.page.toString(),
      perPage: paginationObj.perPage.toString(),
      ...sort,
    });
    const basePath = getResourceBasePath(resource);
    const { data } = await API.get(`${basePath}/list`, {
      params,
    }).catch(apiDataProvider.formatError);
    
    return { data: data.list, total: data.total };
  },

  /**
   * Realiza uma request POST para a criação de um novo item do resource
   *
   * @param resource
   * @param data
   */
  async create(resource: string, { data }: CreateParams) {
    // await apiDataProvider.uploadFiles(data);
    const cleanedData = resource === 'users' ? cleanArrayFields(data) : data;
    const basePath = getResourceBasePath(resource);
    return API.post(basePath, cleanedData).catch(apiDataProvider.formatError);
  },

  async createMany(resource: string, { data }: CreateManyParams) {
    const basePath = getResourceBasePath(resource);
    const response = await API.post(`${basePath}/create-many`, data).catch(apiDataProvider.formatError);
    return { data: response.data };
  },

  /**
   * Realiza uma requisição GET na raiz do resource para receber uma lista com todos os itens do mesmo
   *
   * @param resource
   * @param params
   */
  async getList(resource: string, { filter, pagination, sort }: GetListParams) {
    // Special handling for license-audit-logs
    if (resource === 'license-audit-logs') {
      const paginationObj = pagination || { page: 1, perPage: 50 };
      const params = new URLSearchParams({ 
        ...filter, 
        page: paginationObj.page.toString(),
        perPage: paginationObj.perPage.toString(),
        ...sort 
      });
      const { data } = await API.get(`/admin/licenses/audit-logs/list`, {
        params,
      }).catch(apiDataProvider.formatError);
      return { data: data.list, total: data.total };
    }
    
    // Transformar 'q' em 'registration' para busca de users
    if (resource === 'users' && filter.q) {
      filter = { ...filter, registration: filter.q };
      delete filter.q;
    }
    const paginationObj = pagination || { page: 1, perPage: 10 };
    const params = new URLSearchParams({ 
      ...filter, 
      page: paginationObj.page.toString(),
      perPage: paginationObj.perPage.toString(),
      ...sort 
    });
    const basePath = getResourceBasePath(resource);
    const { data } = await API.get(`${basePath}/list`, {
      params,
    }).catch(apiDataProvider.formatError);
    return { data: data.list, total: data.total };
  },

  /**
   * Realiza uma request PATCH para o id de um resource para o atualizar parcialmente enviando apenas as diferenças do payload
   *
   * @param resource
   * @param changes
   */
  async update(resource: string, { data: currentData, previousData }: Record<string, any>) {
    const cleanedCurrent = resource === 'users' ? cleanArrayFields(currentData) : currentData;

    if (resource === "orders") {
      const body = { ...cleanedCurrent };

      if (Array.isArray(body.items)) {
        const currentItems = normalizeOrderItems(body.items);

        // Backend validation does not allow items: [] in update DTO.
        if (currentItems.length === 0) {
          delete body.items;
        } else {
          const previousItems = normalizeOrderItems(previousData?.items || []);

          // Avoid triggering replace-all when item list did not actually change.
          if (arraysEqual(currentItems, previousItems)) {
            delete body.items;
          } else {
            body.items = currentItems;
          }
        }
      }

      const basePath = getResourceBasePath(resource);
      const { data } = await API.patch(`${basePath}/${previousData.id}`, body).catch(apiDataProvider.formatError);
      return { data };
    }

    const body = pickBy(cleanedCurrent, (value: any, key: any) => {
      const prevValue = previousData[key];
      
      // Se ambos são arrays, comparar conteúdo
      if (Array.isArray(value) && Array.isArray(prevValue)) {
        // Se ambos são arrays vazios, não incluir no body
        if (value.length === 0 && prevValue.length === 0) {
          return false;
        }
        // Comparar conteúdo dos arrays
        return !arraysEqual(value, prevValue);
      }
      
      return prevValue !== value;
    });
    // await apiDataProvider.uploadFiles(body);

    const basePath = getResourceBasePath(resource);
    const { data } = await API.patch(`${basePath}/${previousData.id}`, body).catch(apiDataProvider.formatError);
    return { data };
  },

  async updateManyArray(resource: string, { data, ids }: UpdateManyArrayParams): Promise<UpdateManyResult> {
    const params = new URLSearchParams();
    ids.forEach((id) => params.append("ids", id.toString()));
    const basePath = getResourceBasePath(resource);
    const response = await API.patch(`${basePath}/update-many`, data, {
      params,
    }).catch(apiDataProvider.formatError);
    return { data: response.data };
  },

  /**
   * Realiza uma request DELETE para o id de um resource para o remover determinado registro
   *
   * @param resource
   * @param params
   */
  async delete(resource: string, { id }: DeleteParams): Promise<DeleteResult<any>> {
    if (resource === "orders") {
      const basePath = getResourceBasePath(resource);
      await API.delete(`${basePath}/${id}`).catch(apiDataProvider.formatError);
      return { data: { id } };
    }

    const params = new URLSearchParams();
    params.append("id", id.toString());
    const basePath = getResourceBasePath(resource);
    const { data } = await API.delete(basePath, { params }).catch(apiDataProvider.formatError);

    return { data: { id: data } };
  },

  /**
   * Realiza uma request DELETE para o id de um resource para o remover determinado registro
   *
   * @param resource
   * @param params
   */
  async deleteMany(resource: string, { ids }: DeleteManyParams): Promise<DeleteResult<any>> {
    if (resource === "orders") {
      const basePath = getResourceBasePath(resource);
      await Promise.all(ids.map((id) => API.delete(`${basePath}/${id}`).catch(apiDataProvider.formatError)));
      return { data: ids };
    }

    const params = new URLSearchParams();
    ids.forEach((id) => params.append("id", id.toString()));
    const basePath = getResourceBasePath(resource);
    const { data } = await API.delete(basePath, { params }).catch(apiDataProvider.formatError);

    return { data: [...data] };
  },

  //   /**
  //    * Realiza recursivamente o upload de entidades File/Blob selecionadas para uma url S3 assinada e define a url no atributo
  //    *
  //    * @param  {Record<string} data
  //    * @param  {} any>
  //    */
  //   async uploadFiles(data: Record<string, any>) {
  //     // Não mapear itens que não sejam objeto
  //     if (typeof data !== "object") return;

  //     // eslint-disable-next-line no-restricted-syntax
  //     for (const field in data) {
  //       if (data[field] && data[field].rawFile) {
  //         const file = data[field].rawFile as File & {
  //           arrayBuffer: () => Promise<ArrayBuffer>;
  //         };
  //         const fileMD5sum = crypto
  //           .MD5(crypto.lib.WordArray.create(await file.arrayBuffer()))
  //           .toString();
  //         const filename = `${fileMD5sum}.${file.name.split(".").pop()}`;

  //         const {
  //           data: [{ signedUrl, publicUrl }],
  //         } = await API.post("/files.signedUrls", [
  //           {
  //             path: `uploads/${filename}`,
  //             isPublic: true,
  //             operation: "putObject",
  //           },
  //         ]);

  //         await axios.put(signedUrl, file, {
  //           headers: { "Content-Type": file.type },
  //         });
  //         data[field] = publicUrl;
  //       } else if (Array.isArray(data[field])) {
  //         // Mapear itens de arrays
  //         await Promise.allSettled(
  //           data[field].map((index) =>
  //             apiDataProvider.uploadFiles(data[field][index])
  //           )
  //         );
  //       } else if (typeof data[field] === "object") {
  //         // Mapear objeto encadeado
  //         await apiDataProvider.uploadFiles(data[field]);
  //       }
  //     }
  //   },

  async formatError({ message: commonMessage, response }: AxiosError) {
    const { data, status } = response || {};
    const message = (data && data.error && data.message) || commonMessage;
    throw new HttpError(`[${status || 0}] ${message}`, status, data);
  },
};
